import { __awaiter } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { Banner, Box, StackBlock, StackInline, TextAlpha } from '@yonderland/andes-react';
import { TruckIcon } from '@yonderland/andes-icons';
import { CountrySelector } from '../../components/checkout/delivery/country-selector/CountrySelector';
import { useDispatch, useSelector } from 'react-redux';
import { DeliveryOptions } from '../../components/checkout/delivery/options/DeliveryOptions';
import { chooseDeliveryOption } from '../../util/BuyFormUtil';
import { useMutation, useQuery } from '@tanstack/react-query';
import { checkoutMutations, checkoutQueries } from '@as-react/common-queries';
import { sortCountriesAlphabetically } from '@as-react/common-utils';
import { makeGetBasketDifferentDeliveryPromisesState, makeGetBasketTotalQuantityState, } from '../../redux/basket/selectors/BasketSelector';
import { useCheckoutConfig } from '../../context/CheckoutContext';
import BasketUtil from '../../util/BasketUtil';
import { isAxiosError } from 'axios';
import { useEffect } from 'react';
import { DeliveryEVoucher } from '../../components/checkout/delivery/DeliveryEVoucher';
import { useTranslation } from 'react-i18next';
import { useDeliveryState } from '../../context/CheckoutDeliveryContext';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import SessionStorageUtil from '../../util/SessionStorageUtil';
import { GUEST_USER_CHECKOUT_DATA } from '../../constants/StorageKeysConstants';
import { DeliveryForm } from '../../components/checkout/delivery/DeliveryForm';
import { basketActions } from '../../redux/basket/actions/BasketActions.creators';
const hasMultipleSelectableDeliveryOptions = (info) => info.deliveryOptions.length && info.deliveryOptions.filter(option => !option.exclusionCausedByProduct).length > 1;
export const CheckoutDeliveryContainer = () => {
    const { defaultRequestParams, servicesEndpoint, root, lang } = useAemConfig();
    const [{ info, deliveryCountryId, activeDeliveryOption }, dispatch] = useDeliveryState();
    const { deliveryForm: { singleDeliveryMessage }, } = useCheckoutConfig();
    const { t } = useTranslation(I18N_NAMESPACES.checkout);
    const reduxDispatch = useDispatch();
    const orderLinesHaveDifferentDeliveryPromises = useSelector(makeGetBasketDifferentDeliveryPromisesState());
    const basketTotalQuantity = useSelector(makeGetBasketTotalQuantityState());
    const { getDeliveryCountries, getDeliveryInfo } = checkoutQueries({
        servicesEndpoint,
        defaultRequestParams,
    });
    const { postDeliveryCountry } = checkoutMutations({
        servicesEndpoint,
        defaultRequestParams,
    });
    const { data: deliveryCountries, isLoading: countriesLoading } = useQuery(getDeliveryCountries({
        select: data => sortCountriesAlphabetically(data.countryResourceList, defaultRequestParams.language),
    }));
    const { data: deliveryInfo, error: deliveryInfoError, refetch: refetchGetDeliveryInfo, } = useQuery(getDeliveryInfo(undefined, { enabled: !deliveryCountryId }));
    const { mutate: mutateDeliveryCountry } = useMutation(postDeliveryCountry);
    const { postDeliveryOption } = checkoutMutations({
        servicesEndpoint,
        defaultRequestParams,
    });
    const { mutateAsync: postDeliveryOptionMutation } = useMutation(postDeliveryOption);
    const updateDeliverOption = (name) => __awaiter(void 0, void 0, void 0, function* () {
        yield postDeliveryOptionMutation(name);
        reduxDispatch(basketActions.createGetAction());
    });
    useEffect(() => {
        !!activeDeliveryOption && updateDeliverOption(activeDeliveryOption.name);
    }, [activeDeliveryOption]);
    useEffect(() => {
        var _a;
        if (!deliveryInfo) {
            return;
        }
        if (!deliveryInfo.customerDetails) {
            const userData = SessionStorageUtil.getJsonItem(GUEST_USER_CHECKOUT_DATA);
            if (userData && userData.customerDetails) {
                deliveryInfo.customerDetails = userData.customerDetails;
            }
            if (userData && userData.deliveryAddress) {
                deliveryInfo.deliveryAddress = userData.deliveryAddress;
            }
        }
        const activeDeliveryOption = deliveryInfo.deliveryOptions
            .filter(option => !option.exclusionCausedByProduct)
            .find(option => option.name === deliveryInfo.chosenDeliveryOption);
        if (((_a = deliveryInfo === null || deliveryInfo === void 0 ? void 0 : deliveryInfo.customerDetails) === null || _a === void 0 ? void 0 : _a.idTitle) === -1) {
            deliveryInfo.customerDetails.idTitle = undefined;
        }
        dispatch({ type: 'set_delivery_info', payload: deliveryInfo });
        dispatch({ type: 'set_active_delivery_option', payload: activeDeliveryOption });
        dispatch({ type: 'set_delivery_country_id', payload: deliveryInfo.idCountry });
    }, [deliveryInfo === null || deliveryInfo === void 0 ? void 0 : deliveryInfo.idCountry]);
    if (deliveryInfoError && isAxiosError(deliveryInfoError)) {
        if (BasketUtil.isBasketNotFound(deliveryInfoError) || BasketUtil.isBasketNotInitialized(deliveryInfoError)) {
            location.href = `${root}checkout/basket-overview.html`;
        }
    }
    const handleChangeDeliveryCountry = (countryId) => {
        mutateDeliveryCountry(countryId, {
            onSuccess() {
                if (countryId !== info.idCountry) {
                    refetchGetDeliveryInfo();
                    dispatch({ type: 'set_selected_delivery_address', payload: undefined });
                }
                dispatch({ type: 'set_delivery_country_id', payload: countryId });
            },
            onError(error) {
                if (isAxiosError(error)) {
                    if (BasketUtil.isBasketNotInitialized(error)) {
                        location.href = `${root}checkout/basket-overview.html`;
                    }
                }
            },
        });
    };
    const DeliveryHeader = () => (_jsxs(StackInline, { gap: "4", itemsAlign: "end", children: [_jsx(Box, { flex: { grow: 1 }, children: _jsxs(StackBlock, { itemsAlign: "stretch", gap: "3", children: [_jsx(TextAlpha, { variant: "headingS", children: t('checkout.delivery.title') }), info && info.requiresDelivery && hasMultipleSelectableDeliveryOptions(info) && (_jsx(TextAlpha, { children: t('checkout.delivery.choose.delivery.option') }))] }) }), (info === null || info === void 0 ? void 0 : info.requiresDelivery) && (_jsx(CountrySelector, { countries: deliveryCountries || [], selectedId: deliveryCountryId, loading: countriesLoading, lang: lang, changeDeliveryCountry: handleChangeDeliveryCountry }))] }));
    if (!deliveryInfo) {
        return _jsx(DeliveryHeader, {});
    }
    const { deliveryOptions, requiresDelivery, chosenDeliveryOption } = deliveryInfo;
    const showSingleDeliveryMessage = basketTotalQuantity > 1 && orderLinesHaveDifferentDeliveryPromises && singleDeliveryMessage && requiresDelivery;
    return (_jsxs(StackBlock, { gap: "6", children: [_jsx(DeliveryHeader, {}), showSingleDeliveryMessage && (_jsx(Banner, { testId: "delivery-single-delivery-banner", icon: { source: TruckIcon }, text: singleDeliveryMessage, status: "success" })), _jsxs(DeliveryForm, { children: [!requiresDelivery && _jsx(DeliveryEVoucher, {}), requiresDelivery && deliveryOptions.length > 0 && (_jsx(DeliveryOptions, { deliveryOptions: deliveryOptions, chosenDeliveryOption: chooseDeliveryOption(deliveryOptions, info.chosenDeliveryOption || chosenDeliveryOption) }))] })] }));
};
